// src/modules/myvault/components/automations/actions/WebhookAction.tsx

// What? WEBHOOK editor: method + URL + key/value header table + body textarea.
// Why?  Automation dispatcher accepts `{url, method, body, headers?}`. A
//       typed form prevents JSON body formatting errors and lets admins see
//       headers at a glance.

import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { ActionEditorProps } from "../types";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
type HttpMethod = (typeof METHODS)[number];

const WebhookAction: React.FC<ActionEditorProps> = ({ config, onChange }) => {
  const method = (
    typeof config.method === "string" ? config.method.toUpperCase() : "POST"
  ) as HttpMethod;
  const url = typeof config.url === "string" ? config.url : "";
  const body = typeof config.body === "string" ? config.body : "";
  const headers: Record<string, string> =
    typeof config.headers === "object" && config.headers !== null
      ? (config.headers as Record<string, string>)
      : {};

  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");

  const updateHeader = (k: string, v: string) => {
    const next = { ...headers };
    if (v === "") delete next[k];
    else next[k] = v;
    onChange({ headers: next });
  };

  const removeHeader = (k: string) => {
    const next = { ...headers };
    delete next[k];
    onChange({ headers: next });
  };

  const handleAddHeader = () => {
    const key = newKey.trim();
    if (!key || Object.prototype.hasOwnProperty.call(headers, key)) return;
    onChange({ headers: { ...headers, [key]: newVal } });
    setNewKey("");
    setNewVal("");
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <FormControl sx={{ width: 140 }}>
          <FormLabel>Method</FormLabel>
          <Select
            size="sm"
            value={method}
            onChange={(_, val) =>
              onChange({ method: (val as HttpMethod) || "POST" })
            }
          >
            {METHODS.map((m) => (
              <Option key={m} value={m}>
                {m}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>URL</FormLabel>
          <Input
            size="sm"
            value={url}
            onChange={(e) => onChange({ url: e.target.value })}
            placeholder="https://example.com/hook"
          />
          <FormHelperText>
            Accepts <code>{"{{field}}"}</code> refs; the URL is resolved
            per-trigger.
          </FormHelperText>
        </FormControl>
      </Stack>

      <Box>
        <FormLabel>Headers</FormLabel>
        <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 1, mt: 0.5 }}>
          <Stack spacing={1}>
            {Object.entries(headers).length === 0 && (
              <Typography
                level="body-xs"
                sx={{ color: "text.tertiary", textAlign: "center", py: 1 }}
              >
                No headers configured.
              </Typography>
            )}
            {Object.entries(headers).map(([k, v]) => (
              <Stack key={k} direction="row" spacing={1} alignItems="center">
                <Input
                  size="sm"
                  value={k}
                  disabled
                  sx={{ width: 200, fontFamily: "monospace" }}
                />
                <Input
                  size="sm"
                  value={v}
                  onChange={(e) => updateHeader(k, e.target.value)}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => removeHeader(k)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} alignItems="center">
              <Input
                size="sm"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="Header name"
                sx={{ width: 200, fontFamily: "monospace" }}
              />
              <Input
                size="sm"
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                placeholder="value"
                sx={{ flex: 1 }}
              />
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<AddIcon />}
                onClick={handleAddHeader}
                disabled={
                  !newKey.trim() ||
                  Object.prototype.hasOwnProperty.call(headers, newKey.trim())
                }
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </Sheet>
      </Box>

      {method !== "GET" && method !== "DELETE" && (
        <FormControl>
          <FormLabel>Body</FormLabel>
          <Textarea
            size="sm"
            minRows={4}
            value={body}
            onChange={(e) => onChange({ body: e.target.value })}
            sx={{ fontFamily: "monospace", fontSize: "12px" }}
            placeholder={'{"id": "{{id}}", "status": "changed"}'}
          />
          <FormHelperText>
            Supports <code>{"{{field}}"}</code> refs. JSON is the common content
            type; set Content-Type explicitly in the headers if needed.
          </FormHelperText>
        </FormControl>
      )}
    </Stack>
  );
};

export default WebhookAction;
